import React, {useEffect, useState} from 'react';
import {RadioButton} from "primereact/radiobutton";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {MultiSelect} from "primereact/multiselect";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {exportFile, urlValidator, isCampaignManufacturer, isCampaignDropShipping, isCampaignSeller, isAdmin, userId} from "../../utils/Utils";
import {ExcelOpsCard} from "./ExcelOps";
import ScrapeStatus from "../Product/ScrapeStatus";
import ShopifyProductDialog from "../Campaign/ShopifyProductDialog";
import {buildCategoryImportRequest, defaultImportLimit} from "./importLimit";

import {ProductService} from "../../service/ProductService";
import {ScraperService} from "../../service/ScraperService";
import {ProductExcelImportService} from "../../service/ProductExcelImportService";
import {TemplateService} from "../../service/TemplateService";
import {CampaignService} from "../../service/CampaignService";

const productService = new ProductService();
const scraperService = new ScraperService();
const productExcelImportService = new ProductExcelImportService();
const templateService = new TemplateService();
const campaignService = new CampaignService();

// Consistent action-button width across all surfaces.
const ACTION_BTN_WIDTH = '11em';

// Partial product-update (scraping) fields.
const PARTIAL_FIELDS = [
    {name: 'Name', code: 'name'}, {name: 'Brand', code: 'brand'}, {name: 'Category', code: 'category'},
    {name: 'Description', code: 'description'}, {name: 'Reviews', code: 'reviews'}, {name: 'Image', code: 'image'},
    {name: 'Sku', code: 'sku'}, {name: 'Mpn', code: 'mpn'}, {name: 'Ean', code: 'ean'}, {name: 'Upc', code: 'upc'},
    {name: 'Barcode', code: 'barcode'}, {name: 'Ref ID', code: 'ref_id'}, {name: 'Other Codes', code: 'other_codes'},
];

const ProductsOnboardingStep = ({t, status, refresh, wizardMode}) => {
    const {toast, setError} = React.useContext(AppContext);
    // Sihirbaz içinde admin de son-kullanıcı arayüzünü görür (wizardMode=true).
    // Mevcut OnboardingPage kullanımı bu prop'u geçmez → davranış değişmez.
    const admin = wizardMode ? false : isAdmin();
    const {selectedCampaign} = React.useContext(CampaignContext);

    const [method, setMethod] = useState('link');          // link | excel | xml
    const [linkMode, setLinkMode] = useState('category');   // category | marketplace | xml | single
    const [singleUrl, setSingleUrl] = useState('');
    const [singleErr, setSingleErr] = useState('');
    const [categoryUrl, setCategoryUrl] = useState('');
    // The link waiting in the "how many products" dialog that opens on Add; null = closed.
    // The choice is made per link, there: every product (default) or only its first N
    // in the page's own order (see ./importLimit).
    const [limitDialogUrl, setLimitDialogUrl] = useState(null);
    const [importLimitOn, setImportLimitOn] = useState(false);
    const [importLimit, setImportLimit] = useState(null);
    // The import-list row whose limit is being edited: {id, value}.
    const [editingLimit, setEditingLimit] = useState(null);
    const [xmlUrl, setXmlUrl] = useState('');
    const [loadingImport, setLoadingImport] = useState(false);
    const [showShopifyDialog, setShowShopifyDialog] = useState(false);
    const [importList, setImportList] = useState([]);
    const [loadingImportList, setLoadingImportList] = useState(true);
    // {import_url: active product count} - what each added link brought in.
    const [importUrlCounts, setImportUrlCounts] = useState({});
    const [partialFields, setPartialFields] = useState([]);
    const [partialStatus, setPartialStatus] = useState(null);
    const [shippingStatus, setShippingStatus] = useState(null);

    const loadShippingStatus = () => {
        const id = selectedCampaign?.id;
        if (!id) return;
        scraperService.getLastScrapingRequest(id, 'shipping_price')
            .then(reqs => setShippingStatus(reqs && reqs[0])).catch(() => {});
    };
    // Campaign-wide one-shot job: fetch competitor shipping prices (used in
    // price comparison). Admin + seller/dropshipping only (matches legacy).
    // Uses the dedicated shipping_prices endpoint; passed to ScrapeStatus as a
    // custom trigger (onRun) so one control both runs it and shows status.
    const runShippingPrices = () =>
        scraperService.getShippingPrices({spider_name: 'prices_campaign_update', type: 'shipping_price', campaign_id: selectedCampaign.id});

    const loadPartialStatus = () => {
        const id = selectedCampaign?.id;
        if (!id) return;
        scraperService.getLastScrapingRequest(id, 'products_update_partial')
            .then(reqs => setPartialStatus(reqs && reqs[0])).catch(() => {});
    };
    const partialReq = (spider_name, fields) => ({
        spider_name, type: 'products_update_partial', fields, campaign_id: selectedCampaign.id,
    });

    // Category-page (products_create) + XML feed imports added for this campaign.
    const loadImportList = () => {
        const id = selectedCampaign?.id;
        if (!id) return;
        setLoadingImportList(true);
        Promise.all([
            campaignService.getProductsCreateOrUpdateScrapingRequestsOfCampaigns(id).catch(() => ({})),
            campaignService.getProductImportsOfCampaigns(id).catch(() => ({})),
        ]).then(([createMap, xmlMap]) => {
            const creates = (((createMap && createMap[id]) || {}).scraping_requests || []).map(s => ({...s, import_method: 'url'}));
            const xmls = (((xmlMap && xmlMap[id]) || {}).product_imports || []).map(p => ({...p, import_method: 'xml'}));
            setImportList([...creates, ...xmls]);
        }).catch(() => {}).finally(() => setLoadingImportList(false));
        productService.getImportUrlCounts(id).then(counts => setImportUrlCounts(counts || {})).catch(() => {});
    };

    // Products carry the link they were imported from only for category-page
    // imports, so an XML feed row has no count to show.
    const importedProductCount = (row) => (row.import_method === 'xml' ? null : (importUrlCounts[row.url] || 0));

    // Open the product list filtered down to this one link, in its own tab so
    // the setup wizard the user is working through stays where it is.
    const openImportedProducts = (row) =>
        window.open('#/product?importUrl=' + encodeURIComponent(row.url), '_blank', 'noopener');

    useEffect(() => { loadImportList(); loadPartialStatus(); loadShippingStatus(); }, [selectedCampaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveImportLimit = () => {
        const {id, value} = editingLimit;
        scraperService.updateImportLimit(id, value > 0 ? value : null)
            .then(limit => {
                setImportList(prev => prev.map(r => (r.id === id ? {...r, import_limit: limit} : r)));
                setEditingLimit(null);
                toast.current.show({severity: 'success', summary: t('onboarding.products.link.importLimitSaved', 'Limit saved'),
                    detail: t('onboarding.products.link.importLimitSavedDetail', 'Applies from the next import of this link.')});
            }).catch(error => { setError(error); setError(null); });
    };

    // The limit of an added link, editable in place. Empty = every product of the link.
    const importLimitCell = (row) => {
        if (row.import_method !== 'url') return null;
        if (editingLimit && editingLimit.id === row.id) {
            return (
                <span style={{display: 'inline-flex', gap: 4, alignItems: 'center', marginLeft: 8}}>
                    <InputNumber value={editingLimit.value} min={1} max={100000} inputStyle={{width: 80}}
                                 placeholder={t('onboarding.products.link.importLimitAll', 'All products')}
                                 onValueChange={(e) => setEditingLimit({id: row.id, value: e.value})}/>
                    <Button icon="pi pi-check" className="p-button-sm p-button-text" onClick={saveImportLimit}
                            tooltip={t('actions.save', 'Save')} tooltipOptions={{position: 'bottom'}}/>
                    <Button icon="pi pi-times" className="p-button-sm p-button-text p-button-secondary"
                            onClick={() => setEditingLimit(null)}/>
                </span>
            );
        }
        const label = row.import_limit > 0
            ? t('onboarding.products.link.importLimitTag', 'First {n}').replace('{n}', row.import_limit)
            : t('onboarding.products.link.importLimitAll', 'All products');
        return (
            <Button label={label} icon="pi pi-pencil" iconPos="right"
                    className={'p-button-sm p-button-text' + (row.import_limit > 0 ? '' : ' p-button-secondary')}
                    style={{marginLeft: 8, padding: '0 .4rem'}}
                    tooltip={t('onboarding.products.link.importLimitEdit', 'Change how many products this link imports')}
                    tooltipOptions={{position: 'bottom'}}
                    onClick={() => setEditingLimit({id: row.id, value: row.import_limit || null})}/>
        );
    };

    const deleteImport = (row) => {
        const req = row.import_method === 'xml'
            ? scraperService.deleteProductImport(row.id)
            : scraperService.deleteProductImportUrl(row.id);
        req.then(() => {
            setImportList(prev => prev.filter(r => r.id !== row.id));
            toast.current.show({severity: 'success', summary: t('actions.delete_request', 'Silindi'), detail: t('message_detail.successful')});
        }).catch(error => { setError(error); setError(null); });
    };

    const productCount = (status?.steps || []).find(s => s.key === 'products')?.summary?.count || 0;
    const isShopify = status?.source === 'shopify';

    const methodOptions = [
        {label: t('onboarding.products.method.linkXml', 'Link / XML'), value: 'link', icon: 'pi pi-link'},
        {label: t('onboarding.products.method.excel', 'Excel'), value: 'excel', icon: 'pi pi-file-excel'},
        // Seller URLs and Scraping are admin-only; end users see just Link/XML + Excel.
        ...(admin ? [
            {label: t('onboarding.sites.sellerUrlsTab', 'Seller URLs'), value: 'sellerUrls', icon: 'pi pi-table'},
            {label: t('onboarding.products.scraping', 'Scraping'), value: 'scraping', icon: 'pi pi-sync'},
        ] : []),
    ];

    const done = () => {
        setLoadingImport(false);
        if (refresh) refresh();
    };
    const fail = (error) => {
        setError(error);
        setError(null);
        setLoadingImport(false);
    };
    const ok = (detailKey) => toast.current.show({
        severity: 'success',
        summary: t('onboarding.products.imported', 'İçe aktarıldı'),
        detail: t(detailKey, t('message_detail.successful', 'Başarılı')),
    });

    // ---- #1 single product (same behaviour as /product "Add Product") --------
    const addSingleProduct = () => {
        const urls = singleUrl.split('||').map(u => u.trim()).filter(Boolean);
        const err = urls.length ? urls.map(u => urlValidator(u, t)).find(Boolean) : t('validations.invalid_value');
        if (err) {
            setSingleErr(err);
            return;
        }
        setSingleErr('');
        setLoadingImport(true);
        Promise.all(urls.map(product_url =>
            productService.addProduct({campaign_id: selectedCampaign.id, competitors: [{product_url, is_mine: true}]})
        )).then(() => {
            setSingleUrl('');
            ok('onboarding.products.addedSingle');
            done();
        }).catch(fail);
    };

    // ---- #2 category URL (reuses products_create scraping request) -----------
    // Add opens the dialog; the link is saved (and its import starts) only from there,
    // so a limit is in place before the first run.
    const addCategoryUrl = () => {
        if (!categoryUrl.trim()) return;
        setLimitDialogUrl(categoryUrl.trim());
        setImportLimitOn(false);
        setImportLimit(defaultImportLimit(categoryUrl));
    };

    const confirmAddCategoryUrl = () => {
        setLoadingImport(true);
        scraperService.saveScrapingRequest(buildCategoryImportRequest(limitDialogUrl, selectedCampaign.id, importLimitOn, importLimit))
            .then(() => {
                setCategoryUrl('');
                setLimitDialogUrl(null);
                ok('onboarding.products.categoryStarted');
                loadImportList();
                done();
            }).catch(fail);
    };

    // ---- #3/#4 excel import (reuses ProductExcelImportService) ---------------
    const fullDataImporter = () => {
        if (isCampaignManufacturer(selectedCampaign)) return productExcelImportService.importManufacturerProducts.bind(productExcelImportService);
        if (isCampaignDropShipping(selectedCampaign)) return productExcelImportService.importDropShippingProducts.bind(productExcelImportService);
        return productExcelImportService.importSellerProducts.bind(productExcelImportService);
    };
    const importUrls = (file) => productExcelImportService.importProductsFromUrls(selectedCampaign.id, file).then(() => { if (refresh) refresh(); });
    const importFull = (file) => fullDataImporter()(selectedCampaign.id, file).then(() => { if (refresh) refresh(); });
    const urlsTemplate = () => templateService.exportProductImportFromUrlsExcelTemplate().then(r => exportFile(r, 'product_import_from_urls_template.xlsx'));
    const fullTemplate = () => {
        if (isCampaignManufacturer(selectedCampaign)) return templateService.exportManufacturerProductImportExcelTemplate().then(r => exportFile(r, 'manufacturer_product_import_template.xlsx'));
        if (isCampaignDropShipping(selectedCampaign)) return templateService.exportDropShippingProductImportExcelTemplate().then(r => exportFile(r, 'drop_shipping_product_import_template.xlsx'));
        return templateService.exportSellerProductImportExcelTemplate().then(r => exportFile(r, 'seller_product_import_template.xlsx'));
    };

    // ---- #5 XML feed (reuses saveProductImport) ------------------------------
    const addXml = () => {
        if (!xmlUrl.trim()) return;
        setLoadingImport(true);
        scraperService.saveProductImport(selectedCampaign.id, xmlUrl.trim())
            .then(() => {
                setXmlUrl('');
                ok('onboarding.products.xmlStarted');
                loadImportList();
                done();
            }).catch(fail);
    };

    // ---- shared, consistent "url + action" row -------------------------------
    const urlRow = ({id, value, onChange, onSubmit, label, hint, error, buttonLabel, buttonIcon}) => (
        <div>
            <div style={{display: 'flex', gap: 12, alignItems: 'flex-start'}}>
                <span className="p-float-label" style={{flex: 1}}>
                    <InputText id={id} value={value} onChange={onChange} style={{width: '100%'}}
                               className={error ? 'p-invalid' : ''}
                               onKeyDown={(e) => e.key === 'Enter' && onSubmit()}/>
                    <label htmlFor={id}>{label}</label>
                </span>
                <Button label={buttonLabel} icon={buttonIcon} onClick={onSubmit} disabled={loadingImport}
                        style={{width: ACTION_BTN_WIDTH, flex: '0 0 auto'}}/>
            </div>
            {error
                ? <small style={{color: '#e24c4c'}}>{error}</small>
                : hint ? <small style={{color: '#6b7280'}}>{hint}</small> : null}
        </div>
    );

    const importLimitDialog = (
        <Dialog header={t('onboarding.products.link.importLimitDialogTitle', 'How many products should this link import?')}
                visible={limitDialogUrl !== null} style={{width: '34em', maxWidth: '95vw'}} modal
                onHide={() => { if (!loadingImport) setLimitDialogUrl(null); }}
                footer={<div>
                    <Button label={t('actions.cancel', 'Cancel')} className="p-button-text p-button-secondary"
                            disabled={loadingImport} onClick={() => setLimitDialogUrl(null)}/>
                    <Button label={t('onboarding.products.link.importLimitConfirm', 'Add and import')} icon="pi pi-check"
                            loading={loadingImport} disabled={importLimitOn && !(importLimit > 0)}
                            onClick={confirmAddCategoryUrl}/>
                </div>}>
            <div style={{fontSize: '.9em', color: '#6b7280', wordBreak: 'break-all', marginBottom: 16}}>{limitDialogUrl}</div>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14}}>
                <RadioButton inputId="importLimitAll" checked={!importLimitOn} onChange={() => setImportLimitOn(false)}/>
                <label htmlFor="importLimitAll">
                    <div style={{fontWeight: 500}}>{t('onboarding.products.link.importLimitAll', 'All products')}</div>
                    <small style={{color: '#6b7280'}}>{t('onboarding.products.link.importLimitAllHint', 'Every product on the page is imported.')}</small>
                </label>
            </div>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: 8}}>
                <RadioButton inputId="importLimitFirst" checked={importLimitOn} onChange={() => setImportLimitOn(true)}/>
                <div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                        <label htmlFor="importLimitFirst" style={{fontWeight: 500}}>{t('onboarding.products.link.importLimit', 'Only the first')}</label>
                        <InputNumber inputId="importLimit" value={importLimit} min={1} max={100000} inputStyle={{width: 90}}
                                     onFocus={() => setImportLimitOn(true)}
                                     onValueChange={(e) => { setImportLimit(e.value); setImportLimitOn(true); }}/>
                        <span>{t('onboarding.products.link.importLimitSuffix', 'products, in the order the page lists them')}</span>
                    </div>
                    <small style={{color: '#6b7280'}}>
                        {t('onboarding.products.link.importLimitHint', 'For a sorted list (e.g. best sellers), import only its top products instead of the whole category.')}
                    </small>
                </div>
            </div>
            <small style={{display: 'block', color: '#6b7280', marginTop: 16}}>
                {t('onboarding.products.link.importLimitLater', 'You can change this later from the link list.')}
            </small>
        </Dialog>
    );

    const manualPanel = (
        <div style={{display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 8}}>
            {/* left: method selection, stacked */}
            <div style={{display: 'flex', flexDirection: 'column', gap: 10, minWidth: 180}}>
                <div style={{fontWeight: 500, marginBottom: 2}}>{t('onboarding.products.howAdd', 'Nasıl eklemek istersin?')}</div>
                {methodOptions.map(opt => (
                    <Button key={opt.value} label={opt.label} icon={opt.icon}
                            className={method === opt.value ? '' : 'p-button-outlined p-button-secondary'}
                            style={{justifyContent: 'flex-start'}}
                            onClick={() => setMethod(opt.value)}/>
                ))}
            </div>

            {/* right: active method form */}
            <div style={{flex: 1, minWidth: 320, minHeight: 240, borderLeft: '3px solid #d1d5db', paddingLeft: 28}}>
                {/* LINK */}
                {method === 'link' && (
                    <div>
                        <div style={{display: 'flex', gap: 20, marginBottom: 14, flexWrap: 'wrap'}}>
                            <span><RadioButton inputId="lm_cat" checked={linkMode === 'category'} onChange={() => setLinkMode('category')}/>
                                <label htmlFor="lm_cat" style={{marginLeft: 6}}>{t('onboarding.products.link.category', 'Kategori sayfası')}</label></span>
                            {isCampaignSeller(selectedCampaign) &&
                                <span><RadioButton inputId="lm_mp" checked={linkMode === 'marketplace'} onChange={() => setLinkMode('marketplace')}/>
                                    <label htmlFor="lm_mp" style={{marginLeft: 6}}>{t('onboarding.products.link.marketplace', 'Pazaryeri satıcı linki')}</label></span>}
                            <span><RadioButton inputId="lm_xml" checked={linkMode === 'xml'} onChange={() => setLinkMode('xml')}/>
                                <label htmlFor="lm_xml" style={{marginLeft: 6}}>{t('onboarding.products.method.xml', 'XML feed')}</label></span>
                            <span><RadioButton inputId="lm_single" checked={linkMode === 'single'} onChange={() => setLinkMode('single')}/>
                                <label htmlFor="lm_single" style={{marginLeft: 6}}>{t('onboarding.products.link.single', 'Tek ürün')}</label></span>
                        </div>
                        {linkMode === 'xml'
                            ? urlRow({
                                id: 'xmlUrl', value: xmlUrl,
                                onChange: (e) => setXmlUrl(e.target.value),
                                onSubmit: addXml,
                                label: t('onboarding.products.xml.url', 'XML feed adresi'),
                                hint: t('onboarding.products.xml.hint', 'Ürün feed (XML) adresini yapıştır.'),
                                buttonLabel: t('onboarding.products.xml.connect', 'Bağla'), buttonIcon: 'pi pi-link',
                            })
                            : linkMode === 'single'
                            ? urlRow({
                                id: 'singleUrl', value: singleUrl,
                                onChange: (e) => {
                                    setSingleUrl(e.target.value);
                                    if (singleErr) setSingleErr('');
                                },
                                onSubmit: addSingleProduct,
                                label: t('product.product_url', 'Ürün linki'),
                                hint: t('onboarding.products.link.singleHint', 'Tek ürün sayfasının linkini yapıştır.'),
                                error: singleErr,
                                buttonLabel: t('actions.add', 'Ekle'), buttonIcon: 'pi pi-plus',
                            })
                            : linkMode === 'marketplace'
                            ? urlRow({
                                // Same import as a category page (products_create), just a
                                // clearer label for a marketplace seller's all-products page.
                                id: 'mpUrl', value: categoryUrl,
                                onChange: (e) => setCategoryUrl(e.target.value),
                                onSubmit: addCategoryUrl,
                                label: t('onboarding.products.link.marketplaceUrl', 'Pazaryeri satıcı linki'),
                                hint: t('onboarding.products.link.marketplaceHint', 'Satıcının tüm ürünlerini içeren sayfa; tüm ürünleri içe aktarılır.'),
                                buttonLabel: t('actions.add', 'Ekle'), buttonIcon: 'pi pi-plus',
                            })
                            : urlRow({
                                id: 'catUrl', value: categoryUrl,
                                onChange: (e) => setCategoryUrl(e.target.value),
                                onSubmit: addCategoryUrl,
                                label: t('onboarding.products.link.categoryUrl', 'Kategori sayfası linki'),
                                hint: t('onboarding.products.link.categoryHint', 'Bu sayfadaki tüm ürünler içe aktarılır.'),
                                buttonLabel: t('actions.add', 'Ekle'), buttonIcon: 'pi pi-plus',
                            })}
                        {importLimitDialog}

                        {/* Added Category-page / XML-feed links */}
                        {(
                            <div style={{marginTop: 20}}>
                                <div style={{fontWeight: 700, marginBottom: 10}}>{t('onboarding.products.importList')}</div>
                                <DataTable value={importList} responsiveLayout="scroll" paginator={importList.length > 10} rows={10} dataKey="id"
                                           loading={loadingImportList}
                                           emptyMessage={t('onboarding.products.noImports', 'Kayıt yok')}>
                                    <Column header={t('campaign.import_method')} style={{width: '10em'}}
                                            body={(row) => row.import_method === 'xml'
                                                ? t('onboarding.products.method.xml', 'XML feed')
                                                : t('onboarding.products.link.category', 'Kategori sayfası')}/>
                                    <Column header={t('campaign.link_address')}
                                            body={(row) => <span>
                                                <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>
                                                {importLimitCell(row)}
                                            </span>}/>
                                    <Column header={t('onboarding.products.importedCount')} style={{width: '9em', textAlign: 'center'}}
                                            body={(row) => {
                                                const count = importedProductCount(row);
                                                if (count === null) return '-';
                                                if (count === 0) return <span style={{color: '#6b7280'}}>0</span>;
                                                return <Button label={String(count)} className="p-button-link p-button-sm" style={{padding: 0}}
                                                               tooltip={t('onboarding.products.importedCountTooltip')}
                                                               tooltipOptions={{position: 'bottom'}}
                                                               onClick={() => openImportedProducts(row)}/>;
                                            }}/>
                                    <Column header={t('campaign.last_import_status')} style={{width: '8em', textAlign: 'center'}}
                                            body={(row) => row.import_method === 'url'
                                                ? <ScrapeStatus type="product" scraping_request={row} campaign_id={row.campaign_id}
                                                                product_id={null} competitor_url={null}
                                                                newRequest={{url: row.url, type: 'products_update', spider_name: row.spider_name, campaign_id: row.campaign_id}}
                                                                newRequestCompleted={() => loadImportList()}/>
                                                : '-'}/>
                                    <Column header={t('table_headers.action', 'İşlem')} style={{width: '5em', textAlign: 'center'}}
                                            body={(row) => <Button icon="pi pi-trash" className="p-button-outlined p-button-danger p-button-sm"
                                                                   tooltip={t('actions.remove', 'Kaldır')} tooltipOptions={{position: 'bottom'}}
                                                                   onClick={() => deleteImport(row)}/>}/>
                                </DataTable>
                            </div>
                        )}
                    </div>
                )}

                {/* EXCEL */}
                {method === 'excel' && (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14}}>
                        <ExcelOpsCard t={t} icon="pi pi-link" title={t('onboarding.products.excel.urls', 'Sadece linkler')}
                                      hint={t('onboarding.products.excel.urlsDesc')}
                                      download={urlsTemplate}
                                      uploads={[{labelKey: 'actions.upload', summaryKey: 'onboarding.products.imported', run: importUrls}]}/>
                        <ExcelOpsCard t={t} icon="pi pi-file-excel" title={t('onboarding.products.excel.full', 'Tüm ürün bilgileri')}
                                      hint={t('onboarding.products.excel.fullDesc')}
                                      download={fullTemplate}
                                      uploads={[{labelKey: 'actions.upload', summaryKey: 'onboarding.products.imported', run: importFull}]}/>

                        {admin &&
                            <ExcelOpsCard t={t} icon="pi pi-pencil" title={t('actions.update_product_fields')}
                                          hint={t('onboarding.products.ops.updateFields')}
                                          download={() => templateService.exportUpdateProductsTemplate().then(r => exportFile(r, 'update_products_template.xlsx'))}
                                          uploads={[{labelKey: 'actions.update_product_fields',
                                              run: (file) => productExcelImportService.importUpdateProducts(selectedCampaign.id, file)}]}/>}
                        {/* Aktifleştir/Pasifleştir — kampanya ekleme sihirbazında gizli. */}
                        {!wizardMode &&
                        <ExcelOpsCard t={t} icon="pi pi-power-off" title={t('actions.activate') + ' / ' + t('actions.deactivate')}
                                      hint={t('onboarding.products.ops.activate')}
                                      download={() => templateService.exportProductActivationTemplate().then(r => exportFile(r, 'product_activation_template.xlsx'))}
                                      uploads={[
                                          {labelKey: 'actions.activate', run: (file) => productExcelImportService.activateProducts(selectedCampaign.id, file)},
                                          {labelKey: 'actions.deactivate', run: (file) => productExcelImportService.deactivateProducts(selectedCampaign.id, file)},
                                      ]}/>}
                        {admin && !isCampaignDropShipping(selectedCampaign) && (
                            <ExcelOpsCard t={t} icon="pi pi-users" title={t('actions.upload_product_with_sellers')}
                                          hint={t('onboarding.products.ops.withSellers')}
                                          download={() => templateService.exportProductImportWithSellersExcelTemplate().then(r => exportFile(r, 'product_import_with_sellers_excel_template.xlsx'))}
                                          uploads={[{labelKey: 'actions.upload_product_with_sellers',
                                              run: (file) => productExcelImportService.importProductsWithSellers(selectedCampaign.id, file)}]}/>
                        )}
                        {(userId() === '59b88caf-3041-49f5-b5bd-15aff385f0de' || userId() === '6e904a56-dd36-4717-95e8-23dd5402cde4') && (
                            <ExcelOpsCard t={t} icon="pi pi-file" title={t('actions.upload_product_from_custom_file')}
                                          hint={t('onboarding.products.ops.customFile')}
                                          uploads={[{labelKey: 'actions.upload_product_from_custom_file',
                                              run: (file) => productExcelImportService.importProductsFromCustomFile(selectedCampaign.id, file)}]}/>
                        )}
                        {/* Delete kept last (destructive) — kampanya ekleme sihirbazında gizli. */}
                        {!wizardMode &&
                        <ExcelOpsCard t={t} icon="pi pi-trash" title={t('actions.delete_products')} danger
                                      hint={t('onboarding.products.ops.delete')}
                                      download={() => templateService.exportProductDeletionTemplate().then(r => exportFile(r, 'product_deletion_template.xlsx'))}
                                      uploads={[{labelKey: 'actions.delete_products',
                                          run: (file) => productExcelImportService.deleteProducts(selectedCampaign.id, file)}]}/>}
                    </div>
                )}

                {/* SELLER URLs */}
                {method === 'sellerUrls' && (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14}}>
                        {admin && (
                            <ExcelOpsCard t={t} icon="pi pi-table" title={t('actions.update_sellers_urls_of_products')}
                                          hint={t('onboarding.products.ops.sellerUrlsRow')}
                                          download={() => templateService.exportProductSellersURLsUpdateExcelTemplate().then(r => exportFile(r, 'product_sellers_urls_update_row_excel_template.xlsx'))}
                                          uploads={[{labelKey: 'actions.update_sellers_urls_of_products',
                                              run: (file) => productExcelImportService.updateProductSellerURLs(selectedCampaign.id, file)}]}/>
                        )}
                        {admin && (
                            <ExcelOpsCard t={t} icon="pi pi-table" title={t('actions.update_sellers_urls_of_products_column')}
                                          hint={t('onboarding.products.ops.sellerUrlsColumn')}
                                          download={() => templateService.exportProductSellersURLsUpdateColumnExcelTemplate().then(r => exportFile(r, 'product_sellers_urls_update_column_excel_template.xlsx'))}
                                          uploads={[{labelKey: 'actions.update_sellers_urls_of_products_column',
                                              run: (file) => productExcelImportService.updateProductSellerURLsColumn(selectedCampaign.id, file)}]}/>
                        )}
                        {admin && (
                            <ExcelOpsCard t={t} icon="pi pi-dollar" title={t('actions.update_seller_url_prices')}
                                          hint={t('onboarding.products.ops.sellerUrlPrices')}
                                          download={() => templateService.exportSellerUrlPriceUpdateTemplate().then(r => exportFile(r, 'product_seller_url_price_update_template.xlsx'))}
                                          uploads={[{labelKey: 'actions.update_seller_url_prices',
                                              run: (file) => productExcelImportService.updateSellerUrlPrices(selectedCampaign.id, file),
                                              summary: (result) => ({
                                                  severity: (result?.skipped_count ?? 0) > 0 ? 'warn' : 'success',
                                                  detail: t('onboarding.products.ops.sellerUrlPricesResult', '{u} updated, {s} skipped')
                                                      .replace('{u}', result?.updated_count ?? 0)
                                                      .replace('{s}', result?.skipped_count ?? 0),
                                              })}]}/>
                        )}
                        <ExcelOpsCard t={t} icon="pi pi-link" title={t('actions.update_product_seller_invalid_links')}
                                      hint={t('onboarding.products.ops.sellerInvalid')}
                                      download={() => templateService.exportProductSellerInvalidUrlUpdateTemplate().then(r => exportFile(r, 'product_seller_invalid_url_update_template.xlsx'))}
                                      uploads={[{labelKey: 'actions.update_product_seller_invalid_links',
                                          run: (file) => productExcelImportService.updateSellerInvalidLinks(selectedCampaign.id, file)}]}/>
                    </div>
                )}

                {/* SCRAPING (partial product update) — admin only */}
                {method === 'scraping' && admin && (() => {
                    const card = {border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column', gap: 10};
                    const titleRow = (icon, label) => <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}><i className={icon} style={{color: '#2563eb'}}/> {label}</div>;
                    return (
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14}}>
                            <div style={card}>
                                {titleRow('pi pi-sync', t('onboarding.products.scrapingUpdateFields', 'Update Product Fields'))}
                                <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.products.scrapingUpdateFieldsDesc')}</small>
                                <MultiSelect value={partialFields} options={PARTIAL_FIELDS} optionLabel="name" display="chip" filter
                                             style={{width: '100%'}} placeholder={t('onboarding.products.scrapingFields', 'Alanlar')}
                                             onChange={(e) => setPartialFields(e.value || [])}/>
                                <ScrapeStatus type="product" text={t('onboarding.products.scrapingUpdateFields', 'Update Product Fields')}
                                              scraping_request={partialStatus} campaign_id={selectedCampaign.id}
                                              product_id={null} competitor_url={null} newRequestCompleted={() => loadPartialStatus()}
                                              newRequest={partialReq('products_update_partial', partialFields.map(f => f.code))}/>
                            </div>
                            <div style={card}>
                                {titleRow('pi pi-comments', t('onboarding.products.scrapingReviews', 'Get Product Reviews'))}
                                <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.products.scrapingReviewsDesc')}</small>
                                <ScrapeStatus type="reviews" text={t('onboarding.products.scrapingReviews', 'Get Product Reviews')}
                                              campaign_id={selectedCampaign.id} product_id={null} competitor_url={null}
                                              newRequestCompleted={null} newRequest={partialReq('products_update_review', ['reviews'])}/>
                            </div>
                            <div style={card}>
                                {titleRow('pi pi-replay', t('onboarding.products.scrapingFailedReviews', 'Get Product Failed Reviews'))}
                                <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.products.scrapingFailedReviewsDesc')}</small>
                                <ScrapeStatus type="failed_reviews" text={t('onboarding.products.scrapingFailedReviews', 'Get Product Failed Reviews')}
                                              campaign_id={selectedCampaign.id} product_id={null} competitor_url={null}
                                              newRequestCompleted={null} newRequest={partialReq('products_update_failed_review', ['reviews'])}/>
                            </div>
                            {(isCampaignSeller(selectedCampaign) || isCampaignDropShipping(selectedCampaign)) && (
                                <div style={card}>
                                    {titleRow('pi pi-shopping-cart', t('actions.get_shipping_price', 'Kargo Fiyatlarını Çek'))}
                                    <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.products.scrapingShippingDesc', 'Rakip ürünlerin kargo fiyatlarını tarayıp günceller (fiyat karşılaştırmasında kullanılır).')}</small>
                                    <ScrapeStatus scraping_request={shippingStatus} campaign_id={selectedCampaign.id}
                                                  product_id={null} competitor_url={null}
                                                  onRun={runShippingPrices} runLabel={t('actions.get_shipping_price', 'Kargo Fiyatlarını Çek')}
                                                  newRequestCompleted={() => loadShippingStatus()}/>
                                </div>
                            )}
                        </div>
                    );
                })()}

            </div>
        </div>
    );

    return (
        <div>
            {/* heading left, live count right */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap'}}>
                <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-box"/> {t('onboarding.step.products.label', 'Ürünler')}
                </h2>
                {productCount > 0
                    ? <Message severity="success" text={t('onboarding.products.count', `${productCount} ürün hazır`).replace('{n}', productCount)}/>
                    : <Message severity="info" text={t('onboarding.products.empty', 'Henüz ürün eklenmedi.')}/>}
            </div>

            {/* Shopify conditional status card */}
            {isShopify && (
                <div style={{border: '1px solid #c7e3d4', background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600}}>
                        <i className="pi pi-shopping-cart" style={{color: '#22c55e'}}/>
                        {t('onboarding.products.shopify.title', 'Ürünler Shopify’dan otomatik senkronize ediliyor')}
                    </div>
                    <div style={{color: '#4b5563', marginTop: 6, fontSize: '0.9rem'}}>
                        {t('onboarding.products.shopify.desc', `${productCount} ürün senkron edildi. Manuel ekleme gerekmez.`).replace('{n}', productCount)}
                        {status?.shopify?.shop ? ` · ${status.shopify.shop}` : ''}
                    </div>
                    <div style={{marginTop: 12}}>
                        <Button label={t('onboarding.products.shopify.manage', 'Choose products from your Shopify store')}
                                icon="pi pi-shopping-cart" className="p-button-sm p-button-success"
                                onClick={() => setShowShopifyDialog(true)}/>
                    </div>
                </div>
            )}

            {/* Manual import surfaces are hidden entirely for Shopify campaigns;
                Shopify product selection is handled by the dialog above. */}
            {!isShopify && manualPanel}

            {isShopify &&
                <ShopifyProductDialog visible={showShopifyDialog}
                                      campaignId={selectedCampaign && selectedCampaign.id}
                                      onHide={() => setShowShopifyDialog(false)}
                                      onSaved={() => { if (refresh) refresh(); }}/>}
        </div>
    );
};

export default ProductsOnboardingStep;
